import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Save, RotateCcw } from "lucide-react";
import { api, CompositeWeights } from "../../lib/api";
import { isSuper, useAuth } from "../../lib/auth";

const LABELS: Record<string, string> = {
  activity: "Активність",
  efficiency: "Ефективність",
  care_routing: "Маршрутизація",
  financial_health: "Фін. стійкість",
  dynamics: "Динаміка",
};

export function SettingsPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const canEdit = isSuper(user?.role);

  const { data, isLoading } = useQuery({
    queryKey: ["composite-weights"],
    queryFn: () => api.get<CompositeWeights>("/api/v1/admin/settings/composite-weights"),
  });

  const [w, setW] = useState<Record<string, number> | null>(null);
  useEffect(() => {
    if (data && !w) setW({ ...data.weights });
  }, [data]); // eslint-disable-line

  const save = useMutation({
    mutationFn: (weights: any) =>
      api.put<{ weights: any; sum: number }>("/api/v1/admin/settings/composite-weights", weights),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["composite-weights"] });
      alert("Ваги збережено. Запустіть recalc scores щоб застосувати.");
    },
  });

  if (isLoading || !w) return <div style={{ padding: 24 }}>Завантаження…</div>;

  const sum = Object.values(w).reduce((a, b) => a + b, 0);
  const sumValid = Math.abs(sum - 1.0) < 0.01;

  return (
    <div style={{ padding: "24px 32px", maxWidth: 700 }}>
      <h1 style={{ fontSize: 22, color: "#1a365d", margin: "0 0 4px" }}>Налаштування системи</h1>
      <p style={{ color: "#718096", fontSize: 13, marginBottom: 20 }}>
        Ваги 5 індексів для розрахунку Composite. Сума ваг має дорівнювати 1.0.
        {!canEdit && <span style={{ color: "#e53e3e" }}> Редагувати може лише super_admin.</span>}
      </p>

      <div style={{ background: "white", padding: 24, border: "1px solid #e2e8f0", borderRadius: 8 }}>
        <h2 style={{ fontSize: 16, color: "#2c5282", margin: "0 0 16px" }}>Ваги Composite Score</h2>

        {Object.keys(LABELS).map((k) => (
          <div key={k} style={{ marginBottom: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
              <span style={{ fontSize: 13, fontWeight: 500 }}>{LABELS[k]}</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#2c5282" }}>
                {(w[k] * 100).toFixed(0)}%
              </span>
            </div>
            <input
              type="range"
              min="0" max="1" step="0.01"
              value={w[k]}
              disabled={!canEdit}
              onChange={(e) => setW({ ...w, [k]: parseFloat(e.target.value) })}
              style={{ width: "100%" }}
            />
          </div>
        ))}

        <div style={{
          marginTop: 16, padding: 10,
          background: sumValid ? "#f0fff4" : "#fff5f5",
          border: `1px solid ${sumValid ? "#9ae6b4" : "#feb2b2"}`,
          borderRadius: 4, fontSize: 13,
        }}>
          Сума: <strong>{sum.toFixed(2)}</strong>{" "}
          {sumValid ? "✓ валідна" : "⚠ має бути 1.0"}
        </div>

        {canEdit && (
          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            <button
              onClick={() => save.mutate(w)}
              disabled={!sumValid || save.isPending}
              style={{ ...btnPrimary, opacity: sumValid ? 1 : 0.5 }}
            >
              <Save size={14} /> Зберегти
            </button>
            <button
              onClick={() => setW({ ...data!.default })}
              style={btnSecondary}
            >
              <RotateCcw size={14} /> Скинути до дефолтних
            </button>
          </div>
        )}
      </div>

      <div style={{ marginTop: 16, fontSize: 12, color: "#718096" }}>
        Поточні: {data?.is_default ? "дефолтні" : "налаштовані"} · Дефолт: {Object.entries(data!.default).map(([k, v]) => `${LABELS[k]}=${(v as number) * 100}%`).join(", ")}
      </div>
    </div>
  );
}

const btnPrimary: React.CSSProperties = { padding: "8px 16px", background: "#2c5282", color: "white", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 4, fontWeight: 600 };
const btnSecondary: React.CSSProperties = { padding: "8px 16px", background: "white", color: "#4a5568", border: "1px solid #cbd5e0", borderRadius: 4, cursor: "pointer", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 4 };
